// §embed-bridge — iframe → dashboard postMessage emitter.
//
// The dashboard's `IntakeFormModal` has listened for these three messages since
// its own launch, but nothing ever sent them: this repo emitted no postMessage
// at all until 2026-08-21. Completion was inferred downstream from what did or
// didn't arrive later, which is exactly the inference that cost a week on visit
// #49345585. Contract confirmed against the dashboard's live listener, not
// against a PRD.
//
// ── Origin pinning ────────────────────────────────────────────────────────────
// The parent origin is resolved FROM THE BROWSER, never from a URL param.
// `?returnUrl=` is set by the dashboard's own buildIntakeUrl, so anyone able to
// craft an iframe URL could name the targetOrigin — that defeats the pin as
// thoroughly as '*' does, just less obviously.
//
//   window.location.ancestorOrigins[0]  browser-supplied, cannot be spoofed
//   document.referrer                   can be suppressed, but not forged to
//                                       another origin
//
// The resolved value is then validated against fitpal.com and its subdomains.
// A regex rather than a literal allowlist because tenants are arbitrary
// subdomains (dashboard.fitpal.com, dexaslim.fitpal.com, and whatever the next
// tenant's slug is) provisioned with no intake deploy in between — a hardcoded
// list would silently stop emitting for every tenant created after build date.
//
// Custom tenant domains are anticipated but none are live. One will fail this
// regex and stop emitting, which is why the reject branch is loud: a silent
// non-emit is the same confident-empty-state failure this whole contract exists
// to remove.
export const PARENT_ORIGIN_RE = /^https:\/\/([a-z0-9][a-z0-9-]*\.)?fitpal\.com$/;

// Exported for tests; `warn` is injectable so a caller can route it to a beacon.
export function resolveParentOrigin(win = typeof window !== 'undefined' ? window : undefined, warn = defaultWarn) {
  if (!win) return null;
  // Not embedded at all — a patient on intake.fitpal.com directly. Not an
  // error, and not worth warning about.
  if (win.parent === win) return null;

  let origin = '';
  try {
    const ao = win.location && win.location.ancestorOrigins;
    if (ao && ao.length) origin = ao[0] || '';
  } catch (_) { /* cross-origin read guard */ }

  if (!origin) {
    try {
      const ref = win.document && win.document.referrer;
      if (ref) origin = new URL(ref).origin;
    } catch (_) { /* malformed referrer */ }
  }

  if (!origin) {
    warn('embed-bridge: embedded but parent origin is unknown (no ancestorOrigins, no referrer) — not emitting');
    return null;
  }
  if (!PARENT_ORIGIN_RE.test(origin)) {
    warn(`embed-bridge: parent origin ${origin} is not a fitpal.com origin — not emitting`);
    return null;
  }
  return origin;
}

function defaultWarn(msg) {
  // eslint-disable-next-line no-console
  console.warn(msg);
}

function post(payload, win = typeof window !== 'undefined' ? window : undefined) {
  const origin = resolveParentOrigin(win);
  if (!origin) return false;
  try {
    win.parent.postMessage(payload, origin);
    return true;
  } catch (e) {
    defaultWarn(`embed-bridge: postMessage failed — ${e && e.message}`);
    return false;
  }
}

// ── The three messages the dashboard listens for ─────────────────────────────

// `visitType` MUST be the resolved Beluga visit type (labTestingAutoAuthorization,
// labAnalysis, brandedWeightloss, …) — the same string sent to create-visit-v2,
// never the route name. The dashboard normalizes case and strips [\s_-] before
// matching, so route names like "lab-panel" would only work by coincidence of
// an aliasing that isn't part of the contract.
// The visitType actually sent to Beluga, recorded by the api layer at submit
// time. Reading it here rather than passing a literal from each form removes the
// whole drift class: a form whose visitType changes can't silently keep emitting
// the old one, and no form can emit a route name by mistake.
let lastSubmittedVisitType = null;

export function recordSubmittedVisitType(visitType) {
  if (visitType) lastSubmittedVisitType = visitType;
  return visitType;
}

export function getSubmittedVisitType() {
  return lastSubmittedVisitType;
}

export function emitVisitComplete({ masterId, visitId, visitType, scheduleLink, intakeId, threadId, previousMasterId, pricingBasis, amountCents, fulfillmentRoute, pharmacyChoice, doseChoice } = {}, win) {
  return post(
    {
      type: 'fitpal:visit-complete',
      masterId: masterId ?? null,
      visitId: visitId ?? null,
      visitType: visitType ?? lastSubmittedVisitType ?? null,
      // Optional: present → the dashboard toasts "Schedule your consult to
      // finalize" and opens it; absent → "Visit submitted to your provider".
      ...(scheduleLink ? { scheduleLink } : {}),
      // §advanced-peptide: non-Beluga intakes have no masterId; the dashboard
      // opens the patient's intake + chat thread from these.
      ...(intakeId ? { intakeId } : {}),
      ...(threadId ? { threadId } : {}),
      // §branded-refill (§4): what the follow-up was priced and routed as, so the
      // dashboard's toast/refill row can say "$59 consultation · your pharmacy"
      // without a second fetch. Only present on a quote-driven follow-up.
      ...(previousMasterId ? { previousMasterId } : {}),
      ...(pricingBasis ? { pricingBasis } : {}),
      ...(Number.isFinite(amountCents) ? { amountCents } : {}),
      ...(fulfillmentRoute ? { fulfillmentRoute } : {}),
      ...(pharmacyChoice ? { pharmacyChoice } : {}),
      ...(doseChoice ? { doseChoice } : {}),
    },
    win,
  );
}

// The dashboard reads, in priority order: errors[] (joined ' · '), belugaError,
// msg, error. It also reads errorCode and refundId — a refundId, or an errorCode
// in its refund set, flips the copy to "your card was refunded", which is the
// difference between a patient contacting support and not. So relay the raw
// backend error body rather than a prettified string.
export function emitVisitError(error, { visitType } = {}, win) {
  const data = (error && error.response && error.response.data) || {};
  return post(
    {
      type: 'fitpal:visit-error',
      visitType: visitType ?? lastSubmittedVisitType ?? null,
      ...(Array.isArray(data.errors) && data.errors.length ? { errors: data.errors } : {}),
      ...(data.belugaError ? { belugaError: data.belugaError } : {}),
      // The quote-path rejections say `message`; the dashboard reads `msg`.
      ...(data.msg ? { msg: data.msg } : data.message ? { msg: data.message } : {}),
      ...(data.error ? { error: data.error } : {}),
      ...(data.errorCode ? { errorCode: data.errorCode } : {}),
      // §branded-refill: the quote-path rejections (quote_mismatch,
      // pharmacy_required, invalid_dose) arrive as `code`; relay it so the
      // dashboard's refund copy can key off either spelling.
      ...(data.code ? { code: data.code } : {}),
      ...(data.refunded !== undefined ? { refunded: data.refunded } : {}),
      ...(data.refundId ? { refundId: data.refundId } : {}),
      // Last resort so the dashboard never renders an empty error.
      ...(!data.errors && !data.belugaError && !data.msg && !data.error && error && error.message
        ? { msg: error.message }
        : {}),
    },
    win,
  );
}

export function emitClose(win) {
  return post({ type: 'fitpal:close' }, win);
}

// Photo relays (government ID, Rx photo) are fire-and-forget from the forms. The
// dashboard CLOSES THE MODAL on visit-complete, which unmounts this iframe and
// kills anything still in flight — so emitting the moment the visit is created
// would trade a known-good ID upload for a faster toast.
//
// Wait for them to settle first, but never let a hung upload hold the dashboard
// in a spinner: cap the wait and emit regardless once it expires.
export const PHOTO_SETTLE_CAP_MS = 8000;

// Relays register themselves from apiService.uploadVisitPhoto — the single
// function all nine photo-relaying forms call — so no form has to thread
// promises through its own relay loop.
let photoRelays = [];

export function trackPhotoRelay(promise) {
  if (promise && typeof promise.then === 'function') photoRelays.push(promise);
  return promise;
}

export function awaitPhotoRelays(cap = PHOTO_SETTLE_CAP_MS) {
  const list = photoRelays.filter(Boolean);
  photoRelays = [];
  if (!list.length) return Promise.resolve();
  return Promise.race([
    Promise.allSettled(list),
    new Promise((resolve) => setTimeout(resolve, cap)),
  ]);
}

// What the forms actually call: one line, fire-and-forget, correct ordering.
export function emitVisitCompleteWhenPhotosSettle(payload, win) {
  return awaitPhotoRelays()
    .then(() => emitVisitComplete(payload, win))
    .catch(() => emitVisitComplete(payload, win));
}

// Test seam.
export function __resetPhotoRelays() {
  photoRelays = [];
  lastSubmittedVisitType = null;
}
